import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Play, Pause, SkipForward, SkipBack, Music2, Volume2 } from "lucide-react";
import type { Track } from "@/types/music-provider";

interface UnifiedMusicPlayerProps {
  currentTrack: Track | null;
  isPlaying: boolean;
  position?: number;
  duration?: number;
  volume?: number;
  providerName?: string;
  onPlayPause: () => void;
  onNext: () => void;
  onPrevious: () => void;
  onSeek?: (position: number) => void;
  onVolumeChange?: (volume: number) => void;
}

export const UnifiedMusicPlayer = ({
  currentTrack,
  isPlaying,
  position = 0,
  duration = 0,
  volume = 100,
  providerName = '',
  onPlayPause,
  onNext,
  onPrevious,
  onSeek,
  onVolumeChange
}: UnifiedMusicPlayerProps) => {
  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Card className="bg-card/95 backdrop-blur-md border-border/50">
      <CardContent className="p-4 space-y-4">
        {/* Track Info */}
        <div className="flex items-center gap-3">
          {currentTrack ? (
            <>
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                {currentTrack.album_art ? (
                  <img 
                    src={currentTrack.album_art} 
                    alt={currentTrack.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Music2 className="w-8 h-8 text-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{currentTrack.name}</p>
                <p className="text-xs text-muted-foreground truncate">{currentTrack.artist}</p>
                {currentTrack.album && (
                  <p className="text-xs text-muted-foreground/70 truncate">{currentTrack.album}</p>
                )}
                {providerName && (
                  <p className="text-xs text-primary font-medium mt-1">
                    ♪ {providerName}
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center">
                <Music2 className="w-8 h-8 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Nenhuma música tocando</p>
                <p className="text-xs text-muted-foreground/70">
                  Conecte-se a um serviço de música
                </p>
              </div>
            </>
          )}
        </div>

        {/* Progress Bar */}
        {duration > 0 && onSeek && (
          <div className="space-y-1">
            <Slider
              value={[position]}
              max={duration}
              step={1000}
              onValueChange={([value]) => onSeek(value)}
              className="cursor-pointer"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{formatTime(position)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={onPrevious}
            disabled={!currentTrack}
            className="h-10 w-10"
          >
            <SkipBack className="w-5 h-5" />
          </Button>
          
          <Button
            size="icon"
            onClick={onPlayPause}
            disabled={!currentTrack}
            className="h-14 w-14 bg-gradient-to-br from-primary to-accent hover:opacity-90"
          >
            {isPlaying ? (
              <Pause className="w-7 h-7" fill="white" />
            ) : (
              <Play className="w-7 h-7" fill="white" />
            )}
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={onNext}
            disabled={!currentTrack}
            className="h-10 w-10"
          >
            <SkipForward className="w-5 h-5" />
          </Button>
        </div>

        {/* Volume Control */}
        {onVolumeChange && (
          <div className="flex items-center gap-3">
            <Volume2 className="w-4 h-4 text-muted-foreground" />
            <Slider
              value={[volume]}
              max={100}
              step={1}
              onValueChange={([value]) => onVolumeChange(value)}
              className="flex-1"
            />
            <span className="text-xs text-muted-foreground w-10 text-right">
              {volume}%
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
